import { RefObject } from "react";

import { useComparator } from "../../../comparator.context";

type UseComparatorScrollerConfigParams = {
  maxScrollWidthDisabled: boolean;
  scrollerElementRef: RefObject<HTMLDivElement>;
  scrollableItemWidth: number;
  scrollLeft: number;
};

type UseComparatorScrollerConfig = {
  isSmallViewportWidth: boolean;
  gridGapWidth: number;
  scrollerHorizontalPadding: number;
  scrollerVisibleWidth: number;
  scrollerFullWidth: number;
  scrollPosition: number;
  maxScrollableItems: number;
  maxScrollWidth: number | undefined;
  canScrollPrev: boolean;
  canScrollNext: boolean;
};

function useComparatorScrollerConfig({
  maxScrollWidthDisabled,
  scrollerElementRef,
  scrollableItemWidth,
  scrollLeft,
}: UseComparatorScrollerConfigParams): UseComparatorScrollerConfig {
  const { urlState, selectionCount } = useComparator();
  const { mode } = urlState;

  const isSmallViewportWidth = window.innerWidth <= 1024;

  const gridGapWidth =
    mode === "selection" ? 16 : isSmallViewportWidth ? 32 : 64;

  const scrollerHorizontalPadding =
    mode === "selection" ? 0 : isSmallViewportWidth ? 16 : 32;

  const scrollerVisibleWidth = scrollerElementRef.current?.clientWidth ?? 0;

  const scrollerFullWidth =
    scrollableItemWidth * selectionCount + gridGapWidth * (selectionCount - 1);

  const scrollPosition = Math.round(
    scrollLeft / (scrollableItemWidth + scrollerHorizontalPadding),
  );

  const maxScrollableItems =
    isSmallViewportWidth || maxScrollWidthDisabled ? selectionCount : 3;

  const maxScrollWidth = !maxScrollWidthDisabled
    ? maxScrollableItems * scrollableItemWidth + gridGapWidth
    : undefined;

  const canScrollPrev =
    selectionCount >= maxScrollableItems &&
    (scrollPosition > 0 || scrollLeft > 0);

  const canScrollNext =
    selectionCount >= maxScrollableItems &&
    ((mode === "selection" &&
      scrollerFullWidth - scrollLeft > scrollerVisibleWidth) ||
      (mode === "comparison" &&
        (isSmallViewportWidth
          ? scrollPosition < selectionCount - 1
          : scrollPosition < selectionCount - maxScrollableItems)));

  return {
    isSmallViewportWidth,
    gridGapWidth,
    scrollerHorizontalPadding,
    scrollerVisibleWidth,
    scrollerFullWidth,
    scrollPosition,
    maxScrollableItems,
    maxScrollWidth,
    canScrollPrev,
    canScrollNext,
  };
}

export { type UseComparatorScrollerConfig, useComparatorScrollerConfig };
